"use client";

import React, { useState } from "react";
import Image from "next/image";
import { Title } from "@/components/Common/Typography";
import styles from "../export.module.css";

export interface ProductSpecItem {
  label: string;
  value: string;
  images?: string[];
}

export interface ProductSectionData {
  subtitle?: string;
  title: string;
  robustaTitle: string;
  robustaSpecs: ProductSpecItem[];
  robustaDefaultImg: string;
  arabicaTitle: string;
  arabicaSpecs: ProductSpecItem[];
  arabicaDefaultImg: string;
  excelsaTitle: string;
  excelsaSpecs: ProductSpecItem[];
  excelsaDefaultImg: string;
}

export interface ProductSectionProps {
  data?: ProductSectionData;
}

export default function ProductSection({ data }: ProductSectionProps) {
  const [selectedProductIdx, setSelectedProductIdx] = useState<number>(0);
  const [selectedGrade, setSelectedGrade] = useState<{ [key: number]: string | null }>({
    0: null,
    1: null,
    2: null,
  });

  if (!data || !data.title) return null;

  const getDynamicProductImage = (productIdx: number, gradeVal: string | null): string => {
    const fallback =
      productIdx === 0
        ? data.robustaDefaultImg
        : productIdx === 1
        ? data.arabicaDefaultImg
        : data.excelsaDefaultImg;

    if (!gradeVal) return fallback;

    const parts = gradeVal.split("_");
    const specIdx = parseInt(parts[0], 10);
    const trimmedVal = parts.slice(1).join("_").trim();

    const specs =
      productIdx === 0
        ? data.robustaSpecs
        : productIdx === 1
        ? data.arabicaSpecs
        : data.excelsaSpecs;

    const currentSpec = specs[specIdx];
    if (currentSpec && currentSpec.images) {
      const valList = currentSpec.value.split(",").map((v) => v.trim());
      const valIdx = valList.indexOf(trimmedVal);
      if (valIdx !== -1 && currentSpec.images[valIdx]) {
        return currentSpec.images[valIdx];
      }
    }

    return fallback;
  };

  return (
    <section className={styles.productsSection}>
      {/* Background Decorations */}
      <div className={styles.productDecoWrapper} aria-hidden="true">
        <Image
          src="/images/tea_deco_4.png"
          alt=""
          width={220}
          height={220}
          className={styles.productDecoTopRight}
          unoptimized
        />
        <Image
          src="/images/tea_deco_3.png"
          alt=""
          width={200}
          height={200}
          className={styles.productDecoBottomLeft}
          unoptimized
        />
      </div>

      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <Title
            as="h2"
            size="medium"
            align="center"
            className={styles.sectionTitle}
          >
            {data.title}
          </Title>
        </div>

        {/* Coffee Type Selector Tabs */}
        <div className={styles.productTabsSelector}>
          {[
            { idx: 0, name: "Robusta", num: "01" },
            { idx: 1, name: "Arabica", num: "02" },
            { idx: 2, name: "Excelsa", num: "03" },
          ].map((tab) => (
            <button
              key={tab.idx}
              className={`${styles.productTabSelectBtn} ${
                selectedProductIdx === tab.idx
                  ? styles.activeProductTabSelectBtn
                  : ""
              }`}
              onClick={() => setSelectedProductIdx(tab.idx)}
            >
              <span className={styles.productTabNum}>{tab.num}</span>
              <span className={styles.productTabName}>{tab.name}</span>
            </button>
          ))}
        </div>

        {/* Selected Product Layout Container */}
        <div className={styles.activeProductContainer}>
          {/* Robusta */}
          {selectedProductIdx === 0 && (
            <div className={styles.productDetailRow}>
              <div className={styles.productInfoCol}>
                <div className={styles.productDetailHeader}>
                  <span className={styles.productDetailNum}>01</span>
                  <Title
                    as="h3"
                    size="small"
                    className={styles.productDetailTitle}
                  >
                    {data.robustaTitle}
                  </Title>
                </div>
                <div className={styles.specsList}>
                  {data.robustaSpecs.map((spec, idx) => (
                    <div key={idx} className={styles.specListItem}>
                      <div className={styles.specHeaderRow}>
                        <span className={styles.bulletIcon}>🍃</span>
                        <strong className={styles.specLabel}>{spec.label}</strong>
                      </div>
                      <div className={styles.specTagsWrapper}>
                        {spec.value.split(",").map((val, vIdx) => {
                          const trimmedVal = val.trim();
                          const stateVal = `${idx}_${trimmedVal}`;
                          const isActive = selectedGrade[0] === stateVal;
                          return (
                            <span
                              key={vIdx}
                              className={`${styles.specTag} ${
                                isActive ? styles.activeSpecTag : ""
                              }`}
                              onClick={() => {
                                setSelectedGrade((prev) => ({
                                  ...prev,
                                  0: isActive ? null : stateVal,
                                }));
                              }}
                            >
                              {trimmedVal}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.productMediaCol}>
                <div className={styles.imageContainer}>
                  {getDynamicProductImage(0, selectedGrade[0]) && (
                    <Image
                      key={getDynamicProductImage(0, selectedGrade[0])}
                      src={getDynamicProductImage(0, selectedGrade[0])}
                      alt="Robusta beans"
                      fill
                      unoptimized
                      sizes="(max-width: 768px) 100vw, 50vw"
                      className={styles.mediaImage}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Arabica */}
          {selectedProductIdx === 1 && (
            <div className={styles.productDetailRow}>
              <div className={styles.productInfoCol}>
                <div className={styles.productDetailHeader}>
                  <span className={styles.productDetailNum}>02</span>
                  <Title
                    as="h3"
                    size="small"
                    className={styles.productDetailTitle}
                  >
                    {data.arabicaTitle}
                  </Title>
                </div>
                <div className={styles.specsList}>
                  {data.arabicaSpecs.map((spec, idx) => (
                    <div key={idx} className={styles.specListItem}>
                      <div className={styles.specHeaderRow}>
                        <span className={styles.bulletIcon}>🍃</span>
                        <strong className={styles.specLabel}>{spec.label}</strong>
                      </div>
                      <div className={styles.specTagsWrapper}>
                        {spec.value.split(",").map((val, vIdx) => {
                          const trimmedVal = val.trim();
                          const stateVal = `${idx}_${trimmedVal}`;
                          const isActive = selectedGrade[1] === stateVal;
                          return (
                            <span
                              key={vIdx}
                              className={`${styles.specTag} ${
                                isActive ? styles.activeSpecTag : ""
                              }`}
                              onClick={() => {
                                setSelectedGrade((prev) => ({
                                  ...prev,
                                  1: isActive ? null : stateVal,
                                }));
                              }}
                            >
                              {trimmedVal}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.productMediaCol}>
                <div className={styles.imageContainer}>
                  {getDynamicProductImage(1, selectedGrade[1]) && (
                    <Image
                      key={getDynamicProductImage(1, selectedGrade[1])}
                      src={getDynamicProductImage(1, selectedGrade[1])}
                      alt="Arabica beans"
                      fill
                      unoptimized
                      sizes="(max-width: 768px) 100vw, 50vw"
                      className={styles.mediaImage}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Excelsa */}
          {selectedProductIdx === 2 && (
            <div className={styles.productDetailRow}>
              <div className={styles.productInfoCol}>
                <div className={styles.productDetailHeader}>
                  <span className={styles.productDetailNum}>03</span>
                  <Title
                    as="h3"
                    size="small"
                    className={styles.productDetailTitle}
                  >
                    {data.excelsaTitle}
                  </Title>
                </div>
                <div className={styles.specsList}>
                  {data.excelsaSpecs.map((spec, idx) => (
                    <div key={idx} className={styles.specListItem}>
                      <div className={styles.specHeaderRow}>
                        <span className={styles.bulletIcon}>🍃</span>
                        <strong className={styles.specLabel}>{spec.label}</strong>
                      </div>
                      <div className={styles.specTagsWrapper}>
                        {spec.value.split(",").map((val, vIdx) => {
                          const trimmedVal = val.trim();
                          const stateVal = `${idx}_${trimmedVal}`;
                          const isActive = selectedGrade[2] === stateVal;
                          return (
                            <span
                              key={vIdx}
                              className={`${styles.specTag} ${
                                isActive ? styles.activeSpecTag : ""
                              }`}
                              onClick={() => {
                                setSelectedGrade((prev) => ({
                                  ...prev,
                                  2: isActive ? null : stateVal,
                                }));
                              }}
                            >
                              {trimmedVal}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.productMediaCol}>
                <div className={styles.imageContainer}>
                  {getDynamicProductImage(2, selectedGrade[2]) && (
                    <Image
                      key={getDynamicProductImage(2, selectedGrade[2])}
                      src={getDynamicProductImage(2, selectedGrade[2])}
                      alt="Excelsa beans"
                      fill
                      unoptimized
                      sizes="(max-width: 768px) 100vw, 50vw"
                      className={styles.mediaImage}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
